import { userService } from 'services';
import { CartGroup, DeliveryOption, LocalCart, Order, Product, ProductInLocalCart, ProductInOrder, Role } from './types';
import { cartMethods } from 'business_logic';
import _ from 'lodash';

declare global {
    interface Window {
        dataLayer: any[];
    }
}

const roundMoney = (value: number): number => Math.round(value * 100) / 100;

const EVENT_PARAMS = [
    'source', 'rejected_count', 'merge_cart',
    'order_id', 'order_value', 'order_status', 'action', 'surface', 'currency',
    'items_count', 'points_spent', 'delivery_option', 'promotion',
    'news_id', 'news_title', 'link_url', 'link_type',
    'error_type', 'error_message', 'endpoint', 'status_code', 'reason',
    'page_location', 'page_title'
];

const pushEvent = (payload: { event: string } & { [key: string]: unknown }) => {
    if (window.dataLayer && Array.isArray(window.dataLayer)) {
        window.dataLayer.push({
            ecommerce: null,
            ..._.zipObject(EVENT_PARAMS, _.map(EVENT_PARAMS, () => null))
        });
        window.dataLayer.push(payload);
    }
};

const toCartProduct = (productInOrder: ProductInOrder, cartGroup: CartGroup): ProductInLocalCart => ({
    ...productInOrder,
    unit: productInOrder.product.priceInfo.units.find((u) => u.unit === productInOrder.unit) ??
        { unit: productInOrder.unit, isDefault: false, converter: 1, description: productInOrder.unit },
    mainProductSymkar: null,
    cartGroup,
    systemData: null
});

// GA4 reads the assortment tree from item_category..item_category5. Deeper levels
// have no standard parameter and would be dropped, so they are not sent.
const mapPathToCategories = (path: string[]): { [key: string]: string } => _.reduce(
    _.take(path, 5),
    (categories, segment, index) => ({
        ...categories,
        [index === 0 ? 'item_category' : `item_category${index + 1}`]: segment
    }),
    {} as { [key: string]: string }
);

const mapAdditionsToVariant = (productInCart: ProductInLocalCart): string | undefined => {
    const selected = _.filter(
        productInCart.product.additions,
        (addition, index) => productInCart.additionsSelected?.[index] === true
    );
    return selected.length === 0 ? undefined : _.map(selected, (addition) => addition.name).join(', ');
};

const CART_GROUP_LISTS: { [key in CartGroup]: string } = {
    [CartGroup.main]: 'assortment',
    [CartGroup.isod]: 'isod',
    [CartGroup.gutterings]: 'gutter_creator'
};

const mapToItem = (productInCart: ProductInLocalCart) => ({
    item_id: productInCart.product.symKar,
    item_name: productInCart.product.description,
    price: roundMoney(cartMethods.getProductPriceBeforeDiscountPerDefaultUnit(productInCart)),
    discount: roundMoney(cartMethods.getProductDiscountPerDefaultUnit(productInCart)),
    quantity: cartMethods.getTotalQuantityInDefaultUnit(productInCart),
    unit: cartMethods.getDefaultUnitOfProduct(productInCart.product).unit,
    item_variant: mapAdditionsToVariant(productInCart),
    item_list_name: CART_GROUP_LISTS[productInCart.cartGroup],
    ...mapPathToCategories(productInCart.product.path)
});

export const GA_productViewed = (product: Product) => {
    const price = roundMoney(product.priceInfo.price);
    pushEvent({
        event: 'view_item',
        ecommerce: {
            currency: userService.getUser()?.currency,
            value: price,
            items: [{
                item_id: product.symKar,
                item_name: product.description,
                price,
                discount: roundMoney(product.priceInfo.price * (product.priceInfo.discount / 100)),
                unit: cartMethods.getDefaultUnitOfProduct(product).unit,
                ...mapPathToCategories(product.path)
            }]
        }
    });
};

export type CartSource = 'assortment' | 'isod' | 'gutter_creator' | 'order_copy' | 'order_append';

export const GA_addProductsToCart = (
    products: ProductInLocalCart[],
    source: CartSource,
    rejectedCount: number = 0,
    mergeCart?: boolean
) => {
    pushEvent({
        event: 'add_to_cart',
        source,
        rejected_count: rejectedCount,
        merge_cart: mergeCart,
        ecommerce: {
            currency: userService.getUser()?.currency,
            value: roundMoney(_.sumBy(products, (product) => cartMethods.getTotalPriceGross(product))),
            items: _.map(products, (product) => mapToItem(product))
        }
    });
};

export const GA_removeProductsFromCart = (products: ProductInLocalCart[]) => {
    if (products.length === 0) return;

    pushEvent({
        event: 'remove_from_cart',
        ecommerce: {
            currency: userService.getUser()?.currency,
            value: roundMoney(_.sumBy(products, (product) => cartMethods.getTotalPriceGross(product))),
            items: _.map(products, (product) => mapToItem(product))
        }
    });
};

export const GA_removeProductFromCart = (product: ProductInLocalCart) => GA_removeProductsFromCart([product]);

export const GA_userLoggedIn = () => {
    pushEvent({ event: 'login' });
};

export const GA_appErrorShown = (error: {
    type: 'webservice' | 'too_many_attempts' | 'unidentified';
    message: string;
    endpoint: string;
    statusCode: number;
}) => {
    pushEvent({
        event: 'app_error',
        error_type: error.type,
        error_message: error.message,
        endpoint: error.endpoint,
        status_code: error.statusCode
    });
};

export const GA_serviceUnavailable = (reason: 'service_work' | 'shutdown_hours', endpoint: string) => {
    pushEvent({
        event: 'service_unavailable',
        reason,
        endpoint
    });
};

export const GA_checkoutStarted = (localCart: LocalCart) => {
    pushEvent({
        event: 'begin_checkout',
        ecommerce: {
            currency: userService.getUser()?.currency,
            value: roundMoney(cartMethods.getLocalCartSummaryPrice(localCart)),
            items: _.map(localCart.productsInLocalCart, (product) => mapToItem(product))
        }
    });
};

export const GA_createOrder = (transactionId: string, products: ProductInOrder[], summaryPrice: number, cartGroups: CartGroup[]) => {
    const productsInCart = _.map(products, (product, index) => toCartProduct(product, cartGroups[index]));
    pushEvent({
        event: 'purchase',
        ecommerce: {
            transaction_id: transactionId,
            value: roundMoney(summaryPrice),
            tax: roundMoney(_.sumBy(productsInCart, (product) => cartMethods.getTotalVat(product))),
            currency: userService.getUser()?.currency,
            items: _.map(productsInCart, (product) => mapToItem(product))
        }
    });
};

export const GA_orderRefunded = (transactionId: string, value: number) => {
    pushEvent({
        event: 'refund',
        ecommerce: {
            transaction_id: transactionId,
            value: roundMoney(value),
            currency: userService.getUser()?.currency
        }
    });
};

// A modified order keeps its original NagId, so it must not be reported as a
// purchase - GA4 would count a second transaction under the same id and double the
// revenue. It is not an add_to_cart either: an edit ends in itself, never in a
// purchase, so it would inflate cart additions against nothing.
export const GA_orderModified = (orderId: string, products: ProductInOrder[], cartGroups: CartGroup[]) => {
    const productsInCart = _.map(products, (product, index) => toCartProduct(product, cartGroups[index]));
    pushEvent({
        event: 'order_modified',
        order_id: orderId,
        order_value: roundMoney(_.sumBy(productsInCart, (product) => cartMethods.getTotalPriceGross(product))),
        currency: userService.getUser()?.currency,
        items_count: productsInCart.length
    });
};

export const GA_identifyUser = (user: {
    role: Role;
    companyId: string;
} | null) => {
    if (window.dataLayer && Array.isArray(window.dataLayer)) {
        window.dataLayer.push({
            user_role: user?.role ?? null,
            company_id: user?.companyId ?? null
        });
    }
};

// Page views are pushed explicitly from App.tsx, so the container needs no History
// Change trigger - which would otherwise fire on every state-only history.replace
// this app makes.
export const GA_pageViewed = (path: string, title: string) => {
    pushEvent({
        event: 'page_view',
        page_location: `${window.location.origin}${path}`,
        page_title: title
    });
};

export const GA_newsLinkClicked = (link: {
    newsId: string | null;
    newsTitle: string | null;
    url: string;
    isImage: boolean;
}) => {
    pushEvent({
        event: 'news_click',
        news_id: link.newsId,
        news_title: link.newsTitle,
        link_url: link.url,
        link_type: link.isImage ? 'image' : 'text'
    });
};

export type OrderActionName = 'edit' | 'copy' | 'append' | 'pdf';

export const GA_orderActionUsed = (action: OrderActionName, order: Order, isDesktop: boolean) => {
    pushEvent({
        event: 'order_action',
        action,
        order_id: order.id,
        order_status: order.status,
        order_value: roundMoney(order.gross),
        surface: isDesktop ? 'desktop' : 'mobile'
    });
};

export const GA_isodOpened = () => {
    pushEvent({ event: 'isod_open' });
};

export const GA_promotionOrderPlaced = (order: {
    promotion: string;
    itemsCount: number;
    pointsSpent: number;
    deliveryOption: DeliveryOption | null;
}) => {
    pushEvent({
        event: 'promotion_order',
        promotion: order.promotion,
        items_count: order.itemsCount,
        points_spent: order.pointsSpent,
        delivery_option: order.deliveryOption
    });
};

export const GA_gutterCreatorOpened = () => {
    pushEvent({ event: 'gutter_creator_open' });
};
